import { Card } from '@/components/common/card'
import { DefinitionStat } from '@/components/common/definitionStat'
import type { BorrowMarketAssetData } from '@/utils/internalAppSdk'
import { formatDecimalToPercentage } from '@vaultsfyi/common/utils'
import { BorrowMarketInterestRateModelChart } from './borrowMarketInterestRateModelChart'

interface Props {
  borrowAssetData: BorrowMarketAssetData
}

export const BorrowMarketInterestRateModel = ({ borrowAssetData }: Props) => {
  const { borrowRate, utilizationRate, interestRateModel } = borrowAssetData

  if (!interestRateModel) return null

  const tiles = [
    { label: 'Base rate', value: formatDecimalToPercentage(interestRateModel.baseRate) },
    {
      label: 'Optimal utilization',
      value: formatDecimalToPercentage(interestRateModel.optimalUtilization, { fractionDigits: 0 }),
    },
    { label: 'Slope 1', value: formatDecimalToPercentage(interestRateModel.slope1) },
    { label: 'Slope 2', value: formatDecimalToPercentage(interestRateModel.slope2) },
  ]

  return (
    <Card className="flex flex-col gap-4 bg-surface p-5">
      <div className="flex w-full items-start gap-4">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <h2 className="font-medium text-base text-heading-foreground">Interest rate model</h2>
          <p className="text-sm">Interest rate is dynamic, driven by utilization</p>
        </div>
        <dl>
          <DefinitionStat
            label="Current borrow APY"
            value={borrowRate !== undefined ? formatDecimalToPercentage(borrowRate) : '--'}
            className="shrink-0 gap-0.5 text-right"
            labelClassName="font-normal text-body-foreground text-sm"
          />
        </dl>
      </div>
      <BorrowMarketInterestRateModelChart
        interestRateModel={interestRateModel}
        currentBorrowRate={borrowRate}
        currentUtilizationRate={utilizationRate}
        className="h-60"
      />
      <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {tiles.map((tile) => (
          <DefinitionStat
            key={tile.label}
            label={tile.label}
            value={tile.value}
            className="min-h-20 gap-0.5 overflow-hidden rounded-xl border bg-page p-3"
            labelClassName="font-normal text-body-foreground text-sm"
          />
        ))}
      </dl>
    </Card>
  )
}
